import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';

interface Notification {
  id: number;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  title: string;
  text: string;
  type: string;
}

interface NotificationsResponse {
  notifications: Notification[];
  hasMore: boolean;
}

interface UnreadCountResponse {
  count: number;
}

async function fetchUnreadCount(): Promise<UnreadCountResponse> {
  const response = await apiFetch(ENDPOINTS.NOTIFICATION_UNREAD_COUNT);

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch unread count');
  }

  return response.json();
}

async function fetchNotifications(
  organisationId: number,
  page: number,
): Promise<NotificationsResponse> {
  const response = await apiFetch(
    ENDPOINTS.NOTIFICATION_LIST(organisationId, page),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch notifications');
  }

  return response.json();
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notification', 'unread-count'],
    queryFn: fetchUnreadCount,
  });
}

export function useNotifications(organisationId: number | undefined) {
  return useInfiniteQuery({
    queryKey: ['notification', 'list', organisationId],
    queryFn: ({ pageParam }) => fetchNotifications(organisationId!, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + 1 : undefined,
    enabled: !!organisationId,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await apiFetch(ENDPOINTS.NOTIFICATION_MARK_READ(id), {
        method: 'PATCH',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to mark as read');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification'] });
    },
  });
}

export type { Notification };
